
import { computed, defineComponent, PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { useMutation } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";
import { useToast } from "vue-toastification";

import enUK from "@/locales/project/en-UK.json";

import { CREATE_EXPENSE } from "@/graphql/expenses/mutations";

import FormItem from "@/components/form/FormItem.vue";
import Modal from "@/components/Modal.vue";
import BasicButton from "@/components/BasicButton.vue";

import { Project } from "@/helpers/project/typesDefinition";
import { formatPrice, parsePriceToCents } from "@/helpers/utils";
import dayjs from "dayjs";
import { DocumentNode } from "graphql";

type MessageSchema = typeof enUK;

export default defineComponent({
  props: {
    project: {
      type: Object as PropType<Project>,
      required: true,
    },
    refetchQueries: {
      type: Array as PropType<{ query: DocumentNode; variables?: object }[]>,
      required: false,
      default: () => [],
    },
  },
  setup(props, context) {
    const { t } = useI18n<[MessageSchema], "en-UK">({
      useScope: "global",
      inheritLocale: true,
      messages: {
        "en-UK": enUK,
      },
    });

    const toast = useToast();

    const expenseId = ref("");
    const name = ref("");
    const costNet = ref(0);
    const costGross = ref(0);
    const quantity = ref(0);
    const date = ref(dayjs().format("YYYY-MM-DD"));
    const alreadyInvoiced = ref(false);

    const creationVariables = computed(() => ({
      projectId: props.project.id,
      name: name.value,
      date: date.value,
      costNet: costNet.value,
      costGross: costGross.value,
      quantity: quantity.value,
      alreadyInvoiced: alreadyInvoiced.value,
    }));

    const v$ = useVuelidate(
      {
        projectId: required,
        name: required,
        date: required,
        costNet: required,
        costGross: required,
        quantity: required,
      },
      creationVariables,
      {
        $autoDirty: true,
      },
    );

    const {
      mutate: createExpense,
      onDone: onDoneCreateExpense,
      onError: onErrorCreateExpense,
      loading: loadingCreateExpense,
    } = useMutation(CREATE_EXPENSE, () => ({
      variables: creationVariables.value,
      refetchQueries: props.refetchQueries,
    }));

    onDoneCreateExpense(async (result) => {
      if (result.data?.createExpense?.status === "ok") {
        expenseId.value = result.data.createExpense.data?.id;
        toast.success(t("success.expenses.add"));
        context.emit("close");
      } else {
        const errorResponse = result.data?.createExpense?.error;
        console.log(errorResponse);
        toast.error(t("errors.expenses.add", { message: errorResponse }));
      }
    });

    onErrorCreateExpense((error) => {
      logErrorMessages(error);
      toast.error(t("errors.expenses.add", { message: t("errors.internal") }));
    });

    const loading = loadingCreateExpense.value;

    return {
      loading,
      createExpense,
      formatPrice,
      parsePriceToCents,
      toast,
      name,
      costNet,
      costGross,
      date,
      quantity,
      alreadyInvoiced,
      t,
      v$,
    };
  },
  watch: {},
  emits: ["close"],
  computed: {},
  methods: {
    async submitForm() {
      const isFormCorrect = await this.v$.$validate();
      if (isFormCorrect) {
        return this.createExpense();
      }
    },
    closeModal(): void {
      this.$emit("close");
    },
  },
  components: {
    Modal,
    FormItem,
    BasicButton,
  },
});
